import CommonService from './common.service';
import api from './api';
import { getInfrastructureListingClientId } from '../utils/infrastructureListingClientId';

function triggerCommercialDocumentPdfDownload(blob, filename) {
  const name = filename && String(filename).trim() ? String(filename).trim() : 'commercial-document.pdf';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function commercialDocumentDownloadOrThrow(url, filename) {
  const res = await api.get(url, { responseType: 'blob' });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    let msg = text || 'Download failed';
    try {
      const j = JSON.parse(text);
      if (j && (j.message || j.error)) {
        msg = String(j.message || j.error);
      }
    } catch {
      /* use raw text */
    }
    throw new Error(msg);
  }
  triggerCommercialDocumentPdfDownload(blob, filename);
}

/**
 * @returns {Promise<{ success: boolean, data?: { items: object[] }, message?: string }>}
 */
export async function fetchPublishedInfrastructureListings(limit = 32) {
  return CommonService.get('/infrastructure-leasing/published', { limit }, { silent: true });
}

/**
 * Authenticated seller: published listings from `infrastructure_listing_submission` for the logged-in user only.
 * @param {{ page?: number, pageSize?: number }} params
 * @returns {Promise<{ success: boolean, data?: { items: object[], total: number, page: number, pageSize: number, totalPages: number }, message?: string }>}
 */
export async function fetchMyPublishedInfrastructureListings(params = {}) {
  const { page = 1, pageSize = 12 } = params;
  return CommonService.get('/infrastructure-leasing/my-published', { page, pageSize });
}

/**
 * Buyer: enquiries submitted by the logged-in user (`infrastructure_listing_enquiry`).
 * @param {{ page?: number, pageSize?: number }} params
 */
export async function fetchMyInfrastructureEnquiries(params = {}) {
  const { page = 1, pageSize = 12 } = params;
  return CommonService.get('/infrastructure-leasing/my-enquiries', { page, pageSize });
}

/**
 * Seller: enquiries received for one published listing owned by the current user.
 * @param {number|string} listingId
 * @param {{ page?: number, pageSize?: number }} params
 */
export async function fetchSellerListingEnquiries(listingId, params = {}) {
  const id = listingId == null || listingId === '' ? NaN : Number(listingId);
  if (!Number.isFinite(id) || id < 1) {
    return Promise.reject(new Error('Invalid listing id'));
  }
  const { page = 1, pageSize = 12 } = params;
  return CommonService.get(`/infrastructure-leasing/my-published/${id}/enquiries`, { page, pageSize });
}

/** Seller: payment + invoice summary after buyer completed initial payment (read-only). */
/** Seller: full buyer requirement for an enquiry (requirement-sourced flow only). */
export async function fetchSellerEnquiryBuyerRequirement(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/buyer-requirement`);
}

/** Seller: upsert quotation for requirement-linked enquiry. */
export async function postSellerEnquiryRequirementQuotation(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/requirement-quotation`, body);
}

export async function fetchSellerPaymentInvoiceDetail(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/payment-invoice`);
}

/** Seller: agreement page payload (listing card + enquiry + deal fields). */
export async function fetchInfrastructureAgreementPage(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/agreement`);
}

/** Buyer: read-only agreement page (pending_signature or accepted). */
export async function fetchBuyerInfrastructureAgreementPage(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/agreement`);
}

/** Buyer Phase 9: withdraw own `submitted` enquiry (terminal `withdrawn`). */
export async function postBuyerWithdrawSubmittedEnquiry(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/withdraw`, {});
}

/**
 * Buyer Phase 9: unwind own `pending_signature` deal (same transactional model as seller unwind).
 * Optional body: `{ reason?: string }` for allocation cancel_reason.
 */
export async function postBuyerUnwindPendingSignatureDeal(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/unwind-pending-signature`, body || {});
}

/** Buyer: accept seller quotation on requirement-linked enquiry (creates agreement + reserved allocation). */
export async function postBuyerAcceptRequirementQuotation(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/accept-requirement-quotation`, body);
}

export async function acceptInfrastructureListingDeal(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/accept`, body);
}

async function postSellerRejectSubmittedEnquiry(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/reject`, body);
}

/** Seller: reject deal — submitted enquiry only (same HTTP handler as enquiry reject). */
export async function rejectInfrastructureListingDeal(listingId, enquiryId, body) {
  return postSellerRejectSubmittedEnquiry(listingId, enquiryId, body);
}

/**
 * Seller: reject enquiry with reason / comments (requirement flow & same `/reject` endpoint).
 * Body: `{ reason: string, comments?: string | null }`
 */
export async function rejectInfrastructureListingEnquiry(listingId, enquiryId, body) {
  return postSellerRejectSubmittedEnquiry(listingId, enquiryId, body);
}

export async function signInfrastructureListingAgreement(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/sign`, body || {});
}

/** Buyer: sign after seller has signed (same body shape as seller sign). */
export async function signBuyerInfrastructureListingAgreement(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/sign`, body || {});
}

/** Buyer: payment / invoice / lease pipeline snapshot. */
export async function fetchBuyerPostSaleSnapshot(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/post-sale`);
}

/** Buyer: submit seller review & rating (once per enquiry after payment). */
export async function postBuyerInfrastructureSellerReview(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/seller-review`, body || {});
}

/** Buyer: full lease tracking dashboard (lifecycle, parties, finance, timeline). */
export async function fetchBuyerLeaseTrackingDetail(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/lease-tracking-detail`);
}

/** Phase 22: create gateway-backed payment order (internal ledger only). */
export async function postBuyerCreateInfrastructurePaymentOrder(listingId, enquiryId, payload = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/create-payment-order`, payload || {});
}

export async function getBuyerInfrastructurePaymentSummary(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/payment-summary`);
}

export async function getSellerInfrastructurePaymentSummary(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/payment-summary`);
}

/** Buyer: record initial payment (stub until PSP integration). */
export async function postBuyerInfrastructurePaymentComplete(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/post-sale/payment`, body || {});
}

/** Buyer: activate lease after invoice (idempotent). */
export async function postBuyerInfrastructureLeaseActivate(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/post-sale/lease-activate`, {});
}

/**
 * Seller: save agreement PDF path and/or attachments (upload files via POST /upload/single or /multiple first).
 * @param {object} body — `{ agreementDocumentPath?, attachments?: Array<{ path, name }> | null }`
 */
export async function patchInfrastructureAgreementDocuments(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/agreement/documents`, body);
}

/**
 * @param {number} id
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export async function fetchPublishedInfrastructureById(id) {
  return CommonService.get(`/infrastructure-leasing/published/${id}`);
}

/**
 * @param {{ form: object, step: string }} payload
 * @returns {Promise<{ success: boolean, data?: { infrastructure_listing_id: number, created?: boolean }, message?: string }>}
 */
export async function saveListingDraftAPI(payload) {
  return CommonService.post('/infrastructure-leasing/draft', {
    ...payload,
    clientId: getInfrastructureListingClientId(),
  });
}

/** Buyer Post your requirement — save partial draft (requires auth). */
export async function savePostRequirementDraftAPI(payload) {
  return CommonService.post('/infrastructure-leasing/requirement-draft', payload);
}

/** Buyer Post your requirement — validate + persist as submitted (requires auth). */
export async function submitPostRequirementAPI(payload) {
  return CommonService.post('/infrastructure-leasing/requirement-submit', payload);
}

/** Buyer Post your requirement — load draft (requires auth). `data` is `{ form, updatedAt }` or null. */
export async function fetchPostRequirementDraftAPI() {
  return CommonService.get('/infrastructure-leasing/requirement-draft');
}

/** Buyer dashboard — list saved draft + submitted requirements (`data`: `{ items: [...] }`). */
export async function fetchMyInfrastructureRequirementsAPI() {
  return CommonService.get('/infrastructure-leasing/my-requirements');
}

/** Buyer dashboard — read-only detail for one requirement (`data`: `{ id, status, updatedAt, form }`). */
export async function fetchMyInfrastructureRequirementByIdAPI(requirementId) {
  const id = requirementId == null || requirementId === '' ? NaN : Number(requirementId);
  if (!Number.isFinite(id) || id < 1) {
    return Promise.reject(new Error('Invalid requirement id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-requirements/${id}`);
}

/** Buyer Post your requirement — match published listings (`data`: `{ items, scanned }`). */
export async function postInfrastructureRequirementMatchAPI(requirementPayload) {
  return CommonService.post('/infrastructure-leasing/requirement-match', {
    requirement: requirementPayload,
  });
}

/** Buyer Post your requirement — send the same enquiry payload to multiple listing IDs. */
export async function postInfrastructureRequirementBulkEnquiriesAPI(body) {
  return CommonService.post('/infrastructure-leasing/requirement-bulk-enquiries', body);
}

/**
 * @param {{ form: object, step: string }} payload
 */
export async function publishListingAPI(payload) {
  return CommonService.post('/infrastructure-leasing/publish', {
    ...payload,
    clientId: getInfrastructureListingClientId(),
  });
}

/**
 * Seller: create default inventory line after publish (requires auth).
 * @param {number|string} listingId
 * @param {object} body — see backend `createInventoryLineForSeller`
 */
export async function postMyPublishedListingInventory(listingId, body) {
  const id = listingId == null || listingId === '' ? NaN : Number(listingId);
  if (!Number.isFinite(id) || id < 1) {
    return Promise.reject(new Error('Invalid listing id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${id}/inventory`, body);
}

/** Seller: inventory rows, availability snapshot, allocations, blocks (`GET .../my-published/:listingId/inventory`). */
export async function fetchSellerListingInventoryDetail(listingId) {
  const id = listingId == null || listingId === '' ? NaN : Number(listingId);
  if (!Number.isFinite(id) || id < 1) {
    return Promise.reject(new Error('Invalid listing id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${id}/inventory`);
}

/** Seller: PATCH inventory line (totals, label, UoM, scope, hybrid JSON, default/active flags). */
export async function patchSellerListingInventoryLine(listingId, inventoryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const iid = inventoryId == null || inventoryId === '' ? NaN : Number(inventoryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(iid) || iid < 1) {
    return Promise.reject(new Error('Invalid listing or inventory id'));
  }
  return CommonService.patch(`/infrastructure-leasing/my-published/${lid}/inventory/${iid}`, body);
}

/** Seller: maintenance / hold block on an inventory line. */
export async function postSellerListingInventoryBlock(listingId, inventoryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const iid = inventoryId == null || inventoryId === '' ? NaN : Number(inventoryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(iid) || iid < 1) {
    return Promise.reject(new Error('Invalid listing or inventory id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/inventory/${iid}/blocks`, body);
}

/** Seller: deactivate a block. */
export async function patchSellerListingInventoryBlockDeactivate(listingId, blockId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const bid = blockId == null || blockId === '' ? NaN : Number(blockId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(bid) || bid < 1) {
    return Promise.reject(new Error('Invalid listing or block id'));
  }
  return CommonService.patch(`/infrastructure-leasing/my-published/${lid}/inventory/blocks/${bid}`, {});
}

/**
 * Seller: release reserved allocation.
 * - `pending_signature` → Phase 8 full unwind (delegated server-side).
 * - `accepted` + Phase 10 eligibility → coordinated cancel (inventory + agreement + enquiry).
 * - `accepted` + Phase 11 eligibility → coordinated unpaid cancel (inventory + agreement + enquiry).
 * - Otherwise inventory-only reserved cancel (legacy).
 */
export async function postSellerInventoryAllocationRelease(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/inventory-allocation/release`, body);
}

/** Phase 10: coordinated cancel for `accepted` deal before buyer sign / post-sale (seller-led). */
export async function postSellerCancelAcceptedInfrastructureDeal(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/cancel-accepted-deal`, body || {});
}

/** Phase 11: coordinated cancel for buyer-signed `payment_due` unpaid deals (seller-led, pre-invoice). */
export async function postSellerCancelPaymentDueUnpaidDeal(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/cancel-unpaid-deal`, body || {});
}

/** Phase 12: seller-led early termination for active lease + confirmed default-line allocation (optional body `reason`). */
export async function postSellerTerminateLeaseEarly(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/terminate-lease-early`, body || {});
}

/** Phase 18: buyer-led early termination (same backend semantics as Phase 12; optional body `reason`). */
export async function postBuyerTerminateLeaseEarly(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/terminate-lease-early`, body || {});
}

/** Phase 19: propose mutual early termination (optional body `{ comment?: string }`). */
export async function postBuyerRequestMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/request-mutual-termination`, body || {});
}

/** Phase 19: buyer confirms seller-initiated mutual termination. */
export async function postBuyerConfirmMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/confirm-mutual-termination`, body || {});
}

/** Phase 19: seller proposes mutual early termination (optional body `{ comment?: string }`). */
export async function postSellerRequestMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/request-mutual-termination`, body || {});
}

/** Phase 19: seller confirms buyer-initiated mutual termination. */
export async function postSellerConfirmMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/confirm-mutual-termination`, body || {});
}

/** Phase 20: withdraw pending mutual termination (buyer). */
export async function postBuyerWithdrawMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/withdraw-mutual-termination`, body || {});
}

/** Phase 20: reject pending mutual termination (buyer). Optional body `{ reason?: string }`. */
export async function postBuyerRejectMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/reject-mutual-termination`, body || {});
}

/** Phase 20: withdraw pending mutual termination (seller). */
export async function postSellerWithdrawMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/withdraw-mutual-termination`, body || {});
}

/** Phase 20: reject pending mutual termination (seller). Optional body `{ reason?: string }`. */
export async function postSellerRejectMutualLeaseTermination(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/reject-mutual-termination`, body || {});
}

/** Phase 8: seller unwind `pending_signature` deal (reserved allocation + agreement + enquiry in one backend transaction). */
export async function postUnwindPendingSignatureDeal(listingId, enquiryId, body = {}) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/unwind-pending-signature`, body);
}

/** Seller: extend confirmed allocation + agreement end date. */
export async function postSellerInventoryRenewal(listingId, enquiryId, newLeaseEndDate) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/inventory-renewal`, {
    newLeaseEndDate,
  });
}

/**
 * Submit enquiry for a published listing (requires logged-in user — Bearer token).
 * @param {number|string} listingId
 * @param {object} payload — `{ asset, enquiry, contact }` (optional `listingId` for client parity)
 * @returns {Promise<{ success: boolean, data?: { infrastructure_listing_enquiry_id: number }, message?: string }>}
 */
export async function submitPublishedInfrastructureEnquiry(listingId, payload) {
  const id = listingId == null || listingId === '' ? NaN : Number(listingId);
  if (!Number.isFinite(id) || id < 1) {
    return Promise.reject(new Error('Invalid listing id'));
  }
  return CommonService.post(`/infrastructure-leasing/published/${id}/enquiry`, payload);
}

/**
 * Current user’s latest enquiry for this published listing (requires auth).
 * @param {number|string} listingId
 * @returns {Promise<{ success: boolean, data?: object, message?: string }>}
 */
export async function fetchMyPublishedInfrastructureEnquiry(listingId) {
  const id = listingId == null || listingId === '' ? NaN : Number(listingId);
  if (!Number.isFinite(id) || id < 1) {
    return Promise.reject(new Error('Invalid listing id'));
  }
  return CommonService.get(`/infrastructure-leasing/published/${id}/my-enquiry`);
}

/** Phase 23: buyer commercial documents for an enquiry (list). */
export async function getBuyerCommercialDocuments(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/commercial-documents`);
}

/** Phase 23: buyer commercial document detail (lines + audit). */
export async function getBuyerCommercialDocumentDetail(listingId, enquiryId, documentId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1 || !Number.isFinite(did) || did < 1) {
    return Promise.reject(new Error('Invalid listing, enquiry, or document id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/commercial-documents/${did}`);
}

/**
 * Phase 23: download buyer PDF (blob). Uses axios directly to avoid GlobalService loader on binary responses.
 * @param {string} [filename] — suggested download filename (e.g. documentNumber.pdf)
 */
export async function downloadBuyerCommercialDocument(listingId, enquiryId, documentId, filename) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1 || !Number.isFinite(did) || did < 1) {
    return Promise.reject(new Error('Invalid listing, enquiry, or document id'));
  }
  const url = `/infrastructure-leasing/my-enquiries/${lid}/${eid}/commercial-documents/${did}/download`;
  await commercialDocumentDownloadOrThrow(url, filename);
}

/** Phase 23: seller commercial documents for an enquiry (list). */
export async function getSellerCommercialDocuments(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/commercial-documents`);
}

/** Phase 23: seller commercial document detail. */
export async function getSellerCommercialDocumentDetail(listingId, enquiryId, documentId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1 || !Number.isFinite(did) || did < 1) {
    return Promise.reject(new Error('Invalid listing, enquiry, or document id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/commercial-documents/${did}`);
}

/** Phase 23: download seller PDF (blob). */
export async function downloadSellerCommercialDocument(listingId, enquiryId, documentId, filename) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1 || !Number.isFinite(did) || did < 1) {
    return Promise.reject(new Error('Invalid listing, enquiry, or document id'));
  }
  const url = `/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/commercial-documents/${did}/download`;
  await commercialDocumentDownloadOrThrow(url, filename);
}

/** Phase 24 — disputes (buyer enquiry-scoped). */
export async function fetchBuyerInfrastructureDisputes(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/disputes`);
}

export async function postBuyerInfrastructureDispute(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/disputes`, body || {});
}

export async function fetchBuyerInfrastructureDisputeDetail(listingId, enquiryId, disputeId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1 || !Number.isFinite(did) || did < 1) {
    return Promise.reject(new Error('Invalid ids'));
  }
  return CommonService.get(`/infrastructure-leasing/my-enquiries/${lid}/${eid}/disputes/${did}`);
}

/** Phase 24 — disputes (seller enquiry-scoped). */
export async function fetchSellerInfrastructureDisputes(listingId, enquiryId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/disputes`);
}

export async function postSellerInfrastructureDispute(listingId, enquiryId, body) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1) {
    return Promise.reject(new Error('Invalid listing or enquiry id'));
  }
  return CommonService.post(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/disputes`, body || {});
}

export async function fetchSellerInfrastructureDisputeDetail(listingId, enquiryId, disputeId) {
  const lid = listingId == null || listingId === '' ? NaN : Number(listingId);
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(lid) || lid < 1 || !Number.isFinite(eid) || eid < 1 || !Number.isFinite(did) || did < 1) {
    return Promise.reject(new Error('Invalid ids'));
  }
  return CommonService.get(`/infrastructure-leasing/my-published/${lid}/enquiries/${eid}/disputes/${did}`);
}

/** Phase 24 — dispute thread & evidence (paths relative to /api/v1). */
export async function postInfrastructureDisputeMessage(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/infrastructure-disputes/disputes/${did}/messages`, body || {});
}

export async function postInfrastructureDisputeWithdraw(disputeId) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/infrastructure-disputes/disputes/${did}/withdraw`, {});
}

export async function postInfrastructureDisputeEvidence(disputeId, formData) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  const res = await api.post(`/infrastructure-disputes/disputes/${did}/evidence`, formData);
  return res.data;
}

/** Phase 24 — admin workbench (requires INFRA_PLATFORM_ADMIN_USER_IDS on backend). */
export async function fetchAdminInfrastructureDisputes(params = {}) {
  return CommonService.get('/admin/infrastructure-disputes', params);
}

export async function fetchAdminInfrastructureDisputeDetail(disputeId) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.get(`/admin/infrastructure-disputes/${did}`);
}

export async function postAdminInfrastructureDisputeStatus(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/admin/disputes/${did}/status`, body || {});
}

export async function postAdminInfrastructureDisputeAssign(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/admin/disputes/${did}/assign`, body || {});
}

export async function postAdminInfrastructureDisputeResolve(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/admin/disputes/${did}/resolve`, body || {});
}

export async function postAdminInfrastructureDisputeClose(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/admin/disputes/${did}/close`, body || {});
}

export async function postAdminInfrastructureDisputeAdjustment(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`/admin/disputes/${did}/adjustments`, body || {});
}

export { dispatchInfrastructureNotificationsRefresh } from '../utils/infrastructureNotificationsRefresh';

/** Phase 25 — in-app notification center (paths relative to /api/v1). */
export async function fetchInfrastructureNotifications(params = {}) {
  return CommonService.get('/infrastructure-leasing/my-notifications', params, { silent: true });
}

export async function fetchInfrastructureUnreadNotificationCount() {
  return CommonService.get('/infrastructure-leasing/my-notifications/unread-count', {}, { silent: true });
}

export async function markInfrastructureNotificationRead(notificationId) {
  const nid = notificationId == null || notificationId === '' ? NaN : Number(notificationId);
  if (!Number.isFinite(nid) || nid < 1) return Promise.reject(new Error('Invalid notification id'));
  return CommonService.post(`/infrastructure-leasing/my-notifications/${nid}/read`, {});
}

/** Phase 26 — admin operations console (`INFRA_PLATFORM_ADMIN_USER_IDS` only). Base: `/admin/infrastructure-operations`. */
const OPS = '/admin/infrastructure-operations';

export async function getInfrastructureOpsDashboard(params = {}) {
  return CommonService.get(`${OPS}/dashboard`, params);
}

export async function getInfrastructureOpsLeases(params = {}) {
  return CommonService.get(`${OPS}/leases`, params);
}

export async function getInfrastructureOpsLeaseTimeline(enquiryId) {
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(eid) || eid < 1) return Promise.reject(new Error('Invalid enquiry id'));
  return CommonService.get(`${OPS}/leases/${eid}/timeline`);
}

export async function addInfrastructureOpsLeaseNote(enquiryId, body) {
  const eid = enquiryId == null || enquiryId === '' ? NaN : Number(enquiryId);
  if (!Number.isFinite(eid) || eid < 1) return Promise.reject(new Error('Invalid enquiry id'));
  return CommonService.post(`${OPS}/leases/${eid}/notes`, body || {});
}

export async function getInfrastructureOpsPayments(params = {}) {
  return CommonService.get(`${OPS}/payments`, params);
}

export async function getInfrastructureOpsPaymentTimeline(paymentOrderId) {
  const pid = paymentOrderId == null || paymentOrderId === '' ? NaN : Number(paymentOrderId);
  if (!Number.isFinite(pid) || pid < 1) return Promise.reject(new Error('Invalid payment order id'));
  return CommonService.get(`${OPS}/payments/${pid}/timeline`);
}

export async function addInfrastructureOpsPaymentNote(paymentOrderId, body) {
  const pid = paymentOrderId == null || paymentOrderId === '' ? NaN : Number(paymentOrderId);
  if (!Number.isFinite(pid) || pid < 1) return Promise.reject(new Error('Invalid payment order id'));
  return CommonService.post(`${OPS}/payments/${pid}/notes`, body || {});
}

export async function getInfrastructureOpsSettlements(params = {}) {
  return CommonService.get(`${OPS}/settlements`, params);
}

export async function addInfrastructureOpsSettlementNote(settlementId, body) {
  const sid = settlementId == null || settlementId === '' ? NaN : Number(settlementId);
  if (!Number.isFinite(sid) || sid < 1) return Promise.reject(new Error('Invalid settlement id'));
  return CommonService.post(`${OPS}/settlements/${sid}/notes`, body || {});
}

export async function updateInfrastructureSettlementStatus(settlementId, body) {
  const sid = settlementId == null || settlementId === '' ? NaN : Number(settlementId);
  if (!Number.isFinite(sid) || sid < 1) return Promise.reject(new Error('Invalid settlement id'));
  return CommonService.post(`${OPS}/settlements/${sid}/status`, body || {});
}

export async function getInfrastructureOpsCommercialDocuments(params = {}) {
  return CommonService.get(`${OPS}/commercial-documents`, params);
}

export async function getInfrastructureOpsCommercialDocumentDetail(documentId) {
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid document id'));
  return CommonService.get(`${OPS}/commercial-documents/${did}`);
}

export async function approveInfrastructureCommercialDocument(documentId) {
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid document id'));
  return CommonService.post(`${OPS}/commercial-documents/${did}/approve`, {});
}

export async function finaliseInfrastructureCommercialDocument(documentId) {
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid document id'));
  return CommonService.post(`${OPS}/commercial-documents/${did}/finalise`, {});
}

export async function generateInfrastructureCommercialDocumentPdf(documentId) {
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid document id'));
  return CommonService.post(`${OPS}/commercial-documents/${did}/generate-pdf`, {});
}

export async function cancelInfrastructureCommercialDocument(documentId, body) {
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid document id'));
  return CommonService.post(`${OPS}/commercial-documents/${did}/cancel`, body || {});
}

export async function addInfrastructureOpsCommercialDocumentNote(documentId, body) {
  const did = documentId == null || documentId === '' ? NaN : Number(documentId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid document id'));
  return CommonService.post(`${OPS}/commercial-documents/${did}/notes`, body || {});
}

export async function getInfrastructureOpsDisputes(params = {}) {
  return CommonService.get(`${OPS}/disputes`, params);
}

export async function getInfrastructureOpsDisputeDetail(disputeId) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.get(`${OPS}/disputes/${did}`);
}

export async function assignInfrastructureDispute(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`${OPS}/disputes/${did}/assign`, body || {});
}

export async function updateInfrastructureDisputeStatus(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`${OPS}/disputes/${did}/status`, body || {});
}

export async function resolveInfrastructureDispute(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`${OPS}/disputes/${did}/resolve`, body || {});
}

export async function closeInfrastructureDispute(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`${OPS}/disputes/${did}/close`, body || {});
}

export async function addInfrastructureOpsDisputeNote(disputeId, body) {
  const did = disputeId == null || disputeId === '' ? NaN : Number(disputeId);
  if (!Number.isFinite(did) || did < 1) return Promise.reject(new Error('Invalid dispute id'));
  return CommonService.post(`${OPS}/disputes/${did}/notes`, body || {});
}

export async function getInfrastructureOpsNotifications(params = {}) {
  return CommonService.get(`${OPS}/notifications`, params);
}

export async function addInfrastructureOpsNotificationNote(notificationId, body) {
  const nid = notificationId == null || notificationId === '' ? NaN : Number(notificationId);
  if (!Number.isFinite(nid) || nid < 1) return Promise.reject(new Error('Invalid notification id'));
  return CommonService.post(`${OPS}/notifications/${nid}/notes`, body || {});
}

export async function getInfrastructureOpsExceptions(params = {}) {
  return CommonService.get(`${OPS}/exceptions`, params);
}

export async function getInfrastructureOpsTimeline(params = {}) {
  return CommonService.get(`${OPS}/timeline`, params);
}

/** Dispatches to the correct Phase 26 notes endpoint from module + entity id. */
export async function addInfrastructureOpsNote(module, _entityType, entityId, note) {
  const body = { note };
  const id = Number(entityId);
  if (!Number.isFinite(id) || id < 1) return Promise.reject(new Error('Invalid entity id'));
  const m = String(module || '').toLowerCase();
  if (m === 'lease') return addInfrastructureOpsLeaseNote(id, body);
  if (m === 'payment') return addInfrastructureOpsPaymentNote(id, body);
  if (m === 'settlement') return addInfrastructureOpsSettlementNote(id, body);
  if (m === 'commercial_document') return addInfrastructureOpsCommercialDocumentNote(id, body);
  if (m === 'dispute') return addInfrastructureOpsDisputeNote(id, body);
  if (m === 'notification') return addInfrastructureOpsNotificationNote(id, body);
  return Promise.reject(new Error('Unsupported module for admin note'));
}

/** Phase 27 — read-only reporting (`INFRA_PLATFORM_ADMIN_USER_IDS`). Base: `/admin/infrastructure-reports`. */
const REPORTS = '/admin/infrastructure-reports';

export async function getInfrastructureReportsDashboard(params = {}) {
  return CommonService.get(`${REPORTS}/dashboard`, params);
}

export async function getInfrastructureReportsLeases(params = {}) {
  return CommonService.get(`${REPORTS}/leases`, params);
}

export async function getInfrastructureReportsPayments(params = {}) {
  return CommonService.get(`${REPORTS}/payments`, params);
}

export async function getInfrastructureReportsSettlements(params = {}) {
  return CommonService.get(`${REPORTS}/settlements`, params);
}

export async function getInfrastructureReportsDisputes(params = {}) {
  return CommonService.get(`${REPORTS}/disputes`, params);
}

export async function getInfrastructureReportsDocuments(params = {}) {
  return CommonService.get(`${REPORTS}/documents`, params);
}

export async function getInfrastructureReportsReconciliation(params = {}) {
  return CommonService.get(`${REPORTS}/reconciliation`, params);
}

export async function getInfrastructureReportsSettlementAging(params = {}) {
  return CommonService.get(`${REPORTS}/settlement-aging`, params);
}

export async function getInfrastructureReportsRefundAging(params = {}) {
  return CommonService.get(`${REPORTS}/refund-aging`, params);
}

export async function getInfrastructureReportsDisputeAging(params = {}) {
  return CommonService.get(`${REPORTS}/dispute-aging`, params);
}

export async function getInfrastructureReportsDocumentAging(params = {}) {
  return CommonService.get(`${REPORTS}/document-aging`, params);
}

export async function getInfrastructureReportsRevenue(params = {}) {
  return CommonService.get(`${REPORTS}/revenue`, params);
}

/** CSV export — bypasses CommonService loader; uses bearer token from api interceptor. */
export async function downloadInfrastructureReportCsv(type, params = {}) {
  const t = String(type || '').toLowerCase();
  const res = await api.get(`${REPORTS}/export`, {
    params: { ...params, type: t },
    responseType: 'blob',
  });
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    let msg = text || 'Export failed';
    try {
      const j = JSON.parse(text);
      msg = j.message || j.error || msg;
    } catch {
      /* keep msg */
    }
    throw new Error(msg);
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `infrastructure-report-${t}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
